import { Component, input, output } from '@angular/core';
import type { IconName } from '../icon/icon.component';

/**
 * An action card component that displays an icon, title, and subtitle.
 * Based on Figma design.
 * Figma URL: https://www.figma.com/design/W3VBXvQM2bo98mSNlDSzhi/GivingSpace-Reimagining?node-id=1058-36047
 *
 * @example
 * ```html
 * <!-- Material icon -->
 * <app-action-card
 *   icon="add_circle"
 *   iconType="material"
 *   title="Contribute"
 *   subtitle="Add funds to account"
 *   (clicked)="onContribute()">
 * </app-action-card>
 *
 * <!-- SVG icon -->
 * <app-action-card
 *   icon="/assets/icons/contribute.svg"
 *   iconType="svg"
 *   title="Contribute"
 *   subtitle="Add funds to account"
 *   (clicked)="onContribute()">
 * </app-action-card>
 *
 * <!-- Icon component -->
 * <app-action-card
 *   iconName="activity"
 *   iconType="component"
 *   title="Activity"
 *   subtitle="View transaction history"
 *   (clicked)="onActivity()">
 * </app-action-card>
 * ```
 */
@Component({
  selector: 'app-action-card',
  templateUrl: './action-card.component.html',
  styleUrls: ['./action-card.component.scss'],
  standalone: false,
})
export class ActionCardComponent {
  /**
   * Icon to display - either a Material icon name or path to SVG file
   * Used when iconType is 'material' or 'svg'
   */
  icon = input<string>('');

  /**
   * Icon component name to display (e.g., 'activity', 'external-link')
   * Used when iconType is 'component'
   */
  iconName = input<IconName>();

  /**
   * Color of the icon (CSS color value)
   * Used when iconType is 'component'
   */
  iconColor = input<string>('white');

  /**
   * Size of the icon in pixels
   * Used when iconType is 'component'
   * Default is 16px to match other action card icons
   */
  iconSize = input<number>(16);

  /**
   * Type of icon to display
   * - 'material': Material Design icon (default)
   * - 'svg': SVG file path
   * - 'component': Icon component
   */
  iconType = input<'material' | 'svg' | 'component'>('material');

  /**
   * Title text displayed prominently
   */
  title = input.required<string>();

  /**
   * Subtitle text displayed below the title
   */
  subtitle = input.required<string>();

  /**
   * Whether the action card is disabled
   */
  disabled = input<boolean>(false);

  /**
   * Emits when the action card is clicked
   */
  clicked = output<void>();

  /**
   * Handles click events on the action card
   */
  onClick(): void {
    if (!this.disabled()) {
      this.clicked.emit();
    }
  }

  /**
   * Handles keyboard events for accessibility
   */
  onKeyDown(event: KeyboardEvent): void {
    if ((event.key === 'Enter' || event.key === ' ') && !this.disabled()) {
      event.preventDefault();
      this.clicked.emit();
    }
  }
}
